import moment from 'moment';
import { DateRangeCalculator } from 'components/sharpr/date/dateRangeCalculator/DateRangeCalculator';
import { ensure } from 'components/sharpr/ensure/ensure.js';
import _ from 'lodash';
import { TaxonomyService } from '../../taxonomy/TaxonomyService';
import PostAction from '../../../../../a-react-app/src/_common/domain/enums/PostAction';

/**
 * Service to fetch posts
 * Field names line up with those used by <sh-fine-tune-posts>
 */
export default class FineTunePostsService {
	/**
	 * Injections
	 */
	constructor($q, Api, Cfg, Taxonomy, SavedSearches, AutoArchive, Identity) {
		'ngInject';
		/**
		 * Angular's $q service that gives us Promise.all
		 */
		this.$q = $q;
		/**
		 * The ApiService instance
		 * @type Api
		 */
		this.Api = Api;
		/**
		 * The CfgService instance
		 * @type Cfg
		 */
		this.Cfg = Cfg;
		/**
		 * The taxonomy service
		 * @type TaxonomyService
		 */
		this.Taxonomy = Taxonomy;
		/**
		 * The SavedSearches service
		 * @type SavedSearchesService
		 */
		this.SavedSearches = SavedSearches;
		this.AutoArchive = AutoArchive;
		this.Identity = Identity;
		this.shouldPreserveTime = false;
	}

	/**
	 * Fetch posts matching the given search parameters
	 * @param {Object} search
	 * @property {String} term  The search term
	 * @property {String[]} tags  A list of tag text to filter by
	 * @property {Number[]} categories  A list of category IDs to filter by
	 * @property {String[]} domains  A list of domain names to filter by
	 * @property {String} datePreset  A named date range preset
	 * @property {Date|moment|String} dateStart  Find posts published after this date
	 * @property {Date|moment|String} dateEnd  Find posts published before this date
	 * @property {Boolean} clientIntel  If true, filter to only client-approved posts
	 * @property {Boolean} inStory  If true, filter to only posts part of an Insight Brief; if false, filter
	 *   to posts not part of an Insight Brief; if undefined, do not filter
	 * @property {Boolean} wasEmailed  If true, filter to only posts that were emailed;
	 *   if false, filter to posts that were not emailed; if undefined, do not filter
	 * @property {Boolean} untagged  If true, filter to posts with no tags
	 * @property {Boolean} noInsight  If true, filter to posts with no insight text
	 * @param {Object} options
	 * @returns {Promise}
	 */
	fetch(search, options = {}) {
		this.search = ensure.object(search);
		this.options = ensure.object(options);

		// build search conditions
		const criteria = this.buildSearchCriteria(this.search, this.options);
		const headers = this._getHeaders();
		const shouldSaveSearch = this.hasCriteria(this.search) && (!this.search.notIds || this.search.notIds.length === 0);

		// fetch posts
		const postsPromise = this.Api.request('GET', '/v3/posts/search', criteria, headers).then(response => {
			this.nextPageUrl = response.headers('API-Next-Page');
			this.isLastPage = response.headers('API-Is-Last-Page') === '1';
			this.totalRecords = response.headers('API-Total-Records');
			// then save this search as a recent search
			if (shouldSaveSearch) {
				this.SavedSearches.saveRecent(this);
			}
			this.handleAttachmentQuery(response.data, criteria.term);

			if (options.excludeRestrictedFromShareViaEmailBrief) {
				return response.data.filter(post => post.allowedActions.includes(PostAction.ShareViaEmailBrief));
			} else {
				return response.data;
			}
		});
		const numStoryResults = this.Cfg.readAsInt('Stories.insights_page_results');
		if (!(this.search.page > 1) && this.search.term && numStoryResults > 0 && !this.avoidStoryResults) {
			// TODO: handle multihub searches and update /v3/stories/search to return hub name
			const params = {
				term: this.search.term,
				sort: 'score_date_weighted',
				limit: numStoryResults,
				is_searchable: true,
			};
			const storiesPromise = this.Api.get('/v3/stories/search', params, this._getHeaders());
			return this.$q.all([storiesPromise, postsPromise]).then(([stories, posts]) => {
				const storyPosts = ensure.array(stories).map(story => {
					const collage = [];
					if (story.image) {
						collage.push(story.image);
					}
					if (story.post_images) {
						collage.push(...story.post_images);
					}
					return {
						// <sh-post-pin-intel />, <sh-post-grid-intel />, <sh-post-tabular-intel />
						id: 'story:' + story.id,
						type: 1,
						headline: story.headline,
						is_story: true,
						// <sh-pin-body />
						rich_text_body: '<p>' + story.body.replace(/\n+/g, '</p><p>') + '</p>',
						// <sh-post-credit />, <sh-avatar />
						published_at: story.created_at,
						published_by: story.created_by,
						// <sh-read-more />
						source_link: `${window.BASE_URL}/core/stories/${story.id}?showDetailsLink=1`,
						read_more_text: 'View Insight Brief',
						// <sh-post-pin-intel />, <sh-post-grid-intel />
						photo_collage: collage,
						hub: story.hub,
					};
				});
				return [...storyPosts, ...posts];
			});
		} else {
			return postsPromise;
		}
	}

	fetchFavorites(search, options = {}) {
		search = ensure.object(search);
		search.myFavorites = true;

		const criteria = this.buildSearchCriteria(search, options);
		// build headers
		let headers = {};
		let hubs = ensure.array(this.hubs);
		let multihub = ensure.string(search.multihub);
		if (hubs.length) {
			headers.Hubs = hubs.join(',');
		} else if (multihub.length) {
			headers.Hubs = multihub;
		} else {
			headers.Hubs = 'account';
		}
		// fetch posts
		return this.Api.request('GET', '/v3/posts/search', criteria, headers).then(response => {
			this.nextFavoritePageUrl = response.headers('API-Next-Page');
			this.isLastFavoritePage = response.headers('API-Is-Last-Page') === '1';

			this.handleAttachmentQuery(response.data, criteria.term);

			return response.data;
		});
	}

	/**
	 * Build the search criteria based on the provided search and options.
	 * @param {Object} search
	 * @param {Object} options Optional.
	 * @returns
	 */
	buildSearchCriteria(search, options = {}) {
		// NOTE: By default, the criteria are empty, which is fully legal and expected when the search mode is "mostRecent".
		const criteria = {};

		if (search.mode === 'story') {
			criteria.story_id = ensure.number(search.story_id, 0);
		} else if (search.mode !== 'mostRecent') {
			if (search.term) {
				criteria.term = ensure.string(search.term);
			}

			if (search.tags) {
				const tags = ensure.array(search.tags).filter(t => t !== '(Untagged)');

				if (tags.length) {
					criteria.tags = '#' + tags.join(search.tagsAll ? ' + #' : ', #');
				}
			}

			if (search.categories) {
				criteria.category_names = ensure.array(search.categories).join(';');
			}

			if (search.publishers) {
				criteria.publisher = ensure.array(search.publishers).join(',');
			}

			if (search.domains) {
				criteria.domain_names = ensure.array(search.domains).join(',');
			}

			const dateRange = this.getDateRange(search);

			if (dateRange && dateRange[0]) {
				criteria.since_date = dateRange[0];
			}

			if (dateRange && dateRange[1]) {
				criteria.before_date = dateRange[1];
			}

			if (search.clientIntel) {
				criteria.client_intel = 1;
			}

			const archiveData = this.AutoArchive.getArchiveDataFromSearch(search);

			if (archiveData.archiveAction) {
				criteria.archive_action = archiveData.archiveAction;

				if (archiveData.archiveSinceDate) {
					criteria.archive_since_date = archiveData.archiveSinceDate;
				}
				if (archiveData.archiveBeforeDate) {
					criteria.archive_before_date = archiveData.archiveBeforeDate;
				}
			}

			if (search.archiveAction) {
				criteria.include_deleted =
					search.archiveAction === 'archive past' || search.archiveAction === 'active_and_archived';
			}

			if (search.inStory) {
				criteria.in_story = 1;
			}

			if (search.respectPins) {
				criteria.respect_pins = 1;
			}

			if (search.wasEmailed) {
				criteria.was_emailed = 1;
			}

			if (search.untagged) {
				criteria.untagged = 1;
			}

			if (search.noInsight) {
				criteria.no_insight = 1;
			}

			if (search.datePreset && search.datePreset !== 'all') {
				criteria.date_preset = search.datePreset;
			}

			if (options.notIds || search.notIds) {
				criteria.not_ids = ensure.array(options.notIds || search.notIds).join(',');
			}

			if (options.excludeCopied) {
				criteria.exclude_copied = 1;
			}

			if (search.approvalStatus) {
				criteria.approval_status = search.approvalStatus;
			}

			if (search.classifications) {
				criteria.classification_names = ensure.array(search.classifications).join(';');
			}

			if (options.countOnly) {
				criteria.calculate_total = 1;
			}

			if (search.termBehavior) {
				criteria.term_behavior = search.termBehavior;
			}

			if (search.orderBy) {
				criteria.order_by = search.orderBy;
			}

			if (search.myFavorites) {
				criteria.liked_by = this.Identity.user().id;
			}
		}

		// These extra criteria apply to all modes.
		criteria.limit = ensure.number(search.limit, 100);
		criteria.count_only = options.countOnly ? 1 : 0;

		return criteria;
	}

	fetchNextPage() {
		if (!this.nextPageUrl) {
			// last page didn't have expected headers
			return Promise.resolve([]);
		}
		this.search = ensure.object(this.search);
		const headers = {};
		const multihub = ensure.string(this.search.multihub);
		if (multihub.length) {
			headers.Hubs = multihub;
		}
		return this.Api.request('GET', this.nextPageUrl, null, headers).then(response => {
			this.nextPageUrl = response.headers('API-Next-Page');
			this.isLastPage = response.headers('API-Is-Last-Page') === '1';
			return response.data;
		});
	}

	fetchNextFavoritesPage() {
		this.search = ensure.object(this.search);
		const headers = {};
		const multihub = ensure.string(this.search.multihub);
		if (multihub.length) {
			headers.Hubs = multihub;
		} else {
			headers.Hubs = 'account';
		}
		return this.Api.request('GET', this.nextFavoritePageUrl, null, headers).then(response => {
			this.nextFavoritePageUrl = response.headers('API-Next-Page');
			this.isLastFavoritePage = response.headers('API-Is-Last-Page') === '1';
			return response.data;
		});
	}

	handleAttachmentQuery(posts, term) {
		ensure.array(posts).forEach(post => {
			ensure.array(posts.attachments).forEach(att => {
				att.query = term;
			});
		});
	}

	/**
	 * Check if the given search criteria is "empty"
	 * @param {Object} search  The search parameter
	 * @returns {Boolean}  True if there are any non-default criteria
	 */
	hasCriteria(search) {
		let tags = ensure.array(search.tags).filter(t => t != '(Untagged)');
		let dateRange = this.getDateRange(search);
		return (
			search.term ||
			tags.length ||
			(search.categories && search.categories.length) ||
			(search.domains && search.domains.length) ||
			(dateRange && dateRange.length) ||
			search.clientIntel ||
			search.inStory === true ||
			search.inStory === false ||
			search.wasEmailed == 1 ||
			ensure.array(search.tags).indexOf('(Untagged)') != -1 ||
			search.noInsight ||
			(search.datePreset && search.datePreset != 'all' && search.datePreset != 'custom') ||
			(search.notIds && search.notIds.length) ||
			search.approvalStatus ||
			(search.classifications && search.classifications.length) ||
			(search.publishers && search.publishers.length) ||
			search.wasEmailed === true ||
			search.wasEmailed === false
		);
	}

	/**
	 * Extract the start and end date to use based on the search input
	 * @param {Object} search
	 * @property {String} datePreset  A named date range preset
	 * @property {Date|moment|String} dateStart  Find posts published after this date
	 * @property {Date|moment|String} dateEnd  Find posts published before this date
	 * @returns {moment[]}
	 */
	getDateRange(search) {
		let range;
		if (search.datePreset == 'custom') {
			if (!search.dateStart && !search.dateEnd) {
				// date chooser open but no date selected yet
				return [];
			}
			// Make sure start and end dates are valid
			search.dateStart = moment(search.dateStart).isValid()
				? search.dateStart
				: moment().subtract(30, 'days').format('YYYY-MM-DD');
			search.dateEnd = moment(search.dateEnd).isValid() ? search.dateEnd : moment().format('YYYY-MM-DD');
			range = [moment(search.dateStart), moment(search.dateEnd)];
		} else if (!search.datePreset || search.datePreset == 'all') {
			return [];
		} else {
			range = DateRangeCalculator.runPreset(search.datePreset);
		}

		if (this.shouldPreserveTime) {
			return [range[0].format('YYYY-MM-DD HH:mm:ss'), range[1].format('YYYY-MM-DD HH:mm:ss')];
		} else {
			return [range[0].format('YYYY-MM-DD') + ' 00:00:00', range[1].format('YYYY-MM-DD') + ' 23:59:59'];
		}
	}

	/**
	 * Extract the archive action and archive date preset to use based on the search input
	 * @param {Object} search
	 * @property {String} archiveAction  A named archive action
	 * @property {String} archiveDatePreset  A named archive Date preset
	 * @returns {Object}
	 */
	getArchiveData(search) {
		const archiveData = { archiveAction: undefined, archiveDatePreset: undefined };
		if (search.archiveAction === undefined || search.archiveDatePreset === undefined) {
			return archiveData;
		}
		if (search.archiveAction === 'archive past') {
			archiveData.archiveAction = 'archive';
			archiveData.archiveDatePreset = `last ${search.archiveDatePreset}`;
		} else if (search.archiveAction === 'active_and_archived') {
			archiveData.archiveAction = 'active_and_archived';
			archiveData.archiveDatePreset = `last ${search.archiveDatePreset}`;
		} else {
			if (search.archiveAction === 'archive future') {
				archiveData.archiveAction = 'archive';
			} else {
				archiveData.archiveAction = search.archiveAction;
			}
			archiveData.archiveDatePreset = `next ${search.archiveDatePreset}`;
		}
		return archiveData;
	}

	/**
	 * Get all the criteria arrays ready for display--including categories, tags, users, domains, classifications, allowed clients
	 * @returns {Promise<Object>}  Resolve to an object w/ props categories, tags, users, domains, classifications, allowedClients
	 */
	getCriteriaData(search) {
		search = ensure.object(search);
		this.hubs = search ? ensure.string(search.multihub) : '';
		return this.$q
			.all([
				this._getCategories(),
				this._getTags(),
				this._getUsers(),
				this._getDomains(),
				this._getClassifications(),
				this._getAllowedClients(),
			])
			.then(() => {
				return {
					categories: this.categories,
					tags: this.tags,
					users: this.users,
					domains: this.domains,
					classifications: this.classifications,
					allowedClients: this.allowedClients,
				};
			});
	}

	setPreserveTime(shouldPreserveTime) {
		this.shouldPreserveTime = shouldPreserveTime;
	}

	/**
	 * Load and process categories
	 * @returns {Promise}
	 * @private
	 */
	_getCategories() {
		const getter =
			this.hubs && this.hubs.length
				? this.Api.get('/categories', null, this._getHeaders())
				: this.Taxonomy.getCategories();
		return getter.then(data => {
			this.categories = ensure.array(data).filter(cat => cat.is_active);
			this.categories = _.sortBy(this.categories, 'sort');
			this.categories.unshift({
				id: 0,
				name: 'Uncategorized',
			});
			this.categories = this._groupDuplicates(this.categories);
		});
	}

	/**
	 * Load and process tags
	 * @returns {Promise}
	 * @private
	 */
	_getTags() {
		const getter =
			this.hubs && this.hubs.length ? this.Api.get('/tags', null, this._getHeaders()) : this.Taxonomy.getTags();
		return getter.then(data => {
			this.tags = _.uniq(_.pluck(ensure.array(data), 'name'));
			this.tags.unshift('(Untagged)');
		});
	}

	/**
	 * Load and process users
	 * @returns {Promise}
	 * @private
	 */
	_getUsers() {
		const getter =
			this.hubs && this.hubs.length
				? this.Api.get('/users/publishers', null, this._getHeaders())
				: this.Taxonomy.getPublishers();
		return getter.then(data => {
			this.users = ensure.array(data).map(user => {
				return {
					id: user.id,
					name: user.fname + ' ' + user.lname,
				};
			});
		});
	}

	/**
	 * Load and process domain names
	 * @returns {Promise}
	 * @private
	 */
	_getDomains() {
		const getter =
			this.hubs && this.hubs.length
				? this.Api.get('/posts/domains', null, this._getHeaders())
				: this.Taxonomy.getDomains();
		return getter.then(data => {
			this.domains = ensure.array(data);
		});
	}

	/**
	 * Load and process classifications
	 * @returns {Promise}
	 * @private
	 */
	_getClassifications() {
		const getter =
			this.hubs && this.hubs.length
				? this.Api.get('/classifications', null, this._getHeaders())
				: this.Taxonomy.getClassifications();
		return getter.then(data => {
			this.classifications = this._oldGroupDuplicates(data);
		});
	}

	/**
	 * Gets a list of allowed client records for the logged-in user
	 * @returns {Promise<Object[]>}
	 * @private
	 */
	_getAllowedClients() {
		this.allowedClients = ensure.array(this.Identity.allowedClients());
		return Promise.resolve(this.allowedClients);
	}

	// TODO: this will go away once classifications are handled like categories in multihub search
	_oldGroupDuplicates(data) {
		if (!data) {
			return [];
		}
		const indexed = {};
		data.forEach(item => {
			const lowerName = item.name.toLowerCase();
			if (!indexed[lowerName]) {
				indexed[lowerName] = { name: item.name, id: [] };
			}
			indexed[lowerName].id.push(item.id);
		});
		const deduplicated = [];
		_.forOwn(indexed, item => {
			deduplicated.push({
				id: item.id.join(','),
				name: item.name,
			});
		});
		return _.sortBy(deduplicated, item => item.name.toLowerCase());
	}

	/**
	 * Group duplicate values (for categories and classifications)
	 * @returns {Array}
	 * @private
	 */
	_groupDuplicates(data) {
		if (!data) {
			return [];
		}
		const deduplicated = [];
		data.forEach(item => {
			if (deduplicated.indexOf(item.name) === -1) {
				deduplicated.push(item.name);
			}
		});
		return deduplicated;
	}

	/**
	 * Get proper Hubs header
	 * @returns {null|Object}  The XHR headers to use
	 * @private
	 */
	_getHeaders() {
		if (!this.hubs || this.hubs.length === 0) {
			return null;
		}
		return {
			Hubs: this.hubs,
		};
	}
}
